<script setup lang="ts">
import { computed, ref } from "vue";

/**
 * Game setup, PRD FR-2.
 *
 * Order is changed with arrow controls rather than drag and drop: the PRD
 * records that arrows proved more reliable on mobile, and a drag surface would
 * fight the scroll on a long roster.
 */

import { watch } from "vue";
import type { Player } from "../../domain/store.js";

const props = defineProps<{
  groupName: string;
  roster: Player[];
  celebrations: "all" | "rare";
  hasLiveGame: boolean;
  keepAwake: boolean;
}>();

const emit = defineEmits<{
  start: [order: string[]];
  addPlayer: [name: string];
  removePlayer: [playerId: string];
  back: [];
  stats: [];
  history: [];
  renamePlayer: [playerId: string, name: string];
  renameGroup: [name: string];
  setCelebrations: [mode: "all" | "rare"];
  toggleKeepAwake: [on: boolean];
  discard: [];
}>();

/** Tap the heading to rename the round itself. Same gesture as renaming a
 *  player, so there is one way to correct a name on this screen, not two. */
const editingGroup = ref(false);
const groupDraft = ref("");

function beginGroupEdit(): void {
  groupDraft.value = props.groupName;
  editingGroup.value = true;
}

function commitGroupEdit(): void {
  const name = groupDraft.value.trim();
  // An empty name would leave an unnamed row in the list; keep the old one.
  if (name && name !== props.groupName) emit("renameGroup", name);
  editingGroup.value = false;
}

/** FR-1.4: tap a name to correct it; the change reaches every group and every
 *  game already played, because those hold the id rather than the name. */
const editing = ref<string | null>(null);
const editDraft = ref("");

function beginEdit(id: string, name: string): void {
  editing.value = id;
  editDraft.value = name;
}

function commitEdit(): void {
  const id = editing.value;
  const name = editDraft.value.trim();
  if (id && name) emit("renamePlayer", id, name);
  editing.value = null;
}

interface Entry {
  id: string;
  name: string;
  playing: boolean;
}

/**
 * FR-2.1: the participant list starts as the group roster in its own order.
 * Ordering and sitting out are decisions about *this* game, so they live here
 * rather than being written back to the group.
 */
const players = ref<Entry[]>([]);
const draft = ref("");

watch(
  () => props.roster,
  (roster) => {
    // Names always come fresh from the roster -- reusing the previous entry kept
    // a stale one, so a rename never showed in the list you renamed from. The
    // order and who is sitting out are this screen's own decisions, so they
    // survive: adding a player must not scramble an order already chosen.
    const byId = new Map(roster.map((p) => [p.id, p]));
    const kept = players.value
      .filter((p) => byId.has(p.id))
      .map((p) => ({ ...p, name: byId.get(p.id)!.name }));
    const seen = new Set(kept.map((p) => p.id));
    const added = roster
      .filter((p) => !seen.has(p.id))
      .map((p) => ({ id: p.id, name: p.name, playing: true }));
    players.value = [...kept, ...added];
  },
  { immediate: true, deep: true },
);

/** FR-1.3/2.5: the input stays put so several names go in one after another. */
function add(): void {
  const name = draft.value.trim();
  if (!name) return;
  emit("addPlayer", name);
  draft.value = "";
}

function move(index: number, by: -1 | 1): void {
  const to = index + by;
  if (to < 0 || to >= players.value.length) return;
  const list = players.value;
  const item = list[index];
  const other = list[to];
  if (!item || !other) return;
  list[index] = other;
  list[to] = item;
}

function shuffle(): void {
  const list = players.value;
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = list[i];
    const b = list[j];
    if (a && b) {
      list[i] = b;
      list[j] = a;
    }
  }
}

function remove(index: number): void {
  const entry = players.value[index];
  if (entry) emit("removePlayer", entry.id);
}

/** Who actually begins: the first entry still playing, not simply row one. */
const starterId = computed(() => players.value.find((p) => p.playing)?.id ?? null);

function start(): void {
  const playing = players.value.filter((p) => p.playing);
  // FR-2.6: at least two, as recommended.
  if (playing.length < 2) return;
  emit(
    "start",
    playing.map((p) => p.id),
  );
}
</script>

<template>
  <div class="setup">
    <header class="nav">
      <button
        class="nav-btn"
        @click="emit('back')"
      >
        ‹ Runden
      </button>
      <span class="nav-right">
        <button
          class="nav-btn"
          @click="emit('history')"
        >
          Verlauf
        </button>
        <button
          class="nav-btn"
          @click="emit('stats')"
        >
          Statistik
        </button>
      </span>
    </header>
    <header class="head">
      <input
        v-if="editingGroup"
        v-model="groupDraft"
        class="rename-group"
        aria-label="Runde umbenennen"
        enterkeyhint="done"
        @keyup.enter="commitGroupEdit"
        @blur="commitGroupEdit"
      >
      <h1 v-else>
        <button
          class="group-name"
          :aria-label="`${props.groupName} umbenennen`"
          @click="beginGroupEdit"
        >
          {{ props.groupName }}
        </button>
      </h1>
      <p class="sub">
        Wer spielt mit?
      </p>
    </header>

    <form
      class="add"
      @submit.prevent="add"
    >
      <input
        v-model="draft"
        class="input"
        type="text"
        placeholder="Name"
        autocomplete="off"
        enterkeyhint="done"
        aria-label="Spielername"
      >
      <button
        class="add-btn"
        type="submit"
        :disabled="draft.trim() === ''"
      >
        Hinzufügen
      </button>
    </form>

    <ol class="list">
      <li
        v-for="(p, i) in players"
        :key="p.id"
        class="row"
        :class="{ out: !p.playing }"
      >
        <span class="pos num">{{ i + 1 }}</span>
        <span
          v-if="p.id === starterId"
          class="starter"
        >Start</span>
        <input
          v-if="editing === p.id"
          v-model="editDraft"
          class="rename"
          aria-label="Name ändern"
          enterkeyhint="done"
          @keyup.enter="commitEdit"
          @blur="commitEdit"
        >
        <button
          v-else
          class="name"
          :aria-label="`${p.name} umbenennen`"
          @click="beginEdit(p.id, p.name)"
        >
          {{ p.name }}
        </button>
        <button
          class="icon"
          :disabled="i === 0"
          aria-label="Nach oben"
          @click="move(i, -1)"
        >
          <svg
            class="ico"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="m6 14 6-6 6 6" />
          </svg>
        </button>
        <button
          class="icon"
          :disabled="i === players.length - 1"
          aria-label="Nach unten"
          @click="move(i, 1)"
        >
          <svg
            class="ico"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="m6 10 6 6 6-6" />
          </svg>
        </button>
        <button
          class="icon"
          :aria-label="p.playing ? 'Aussetzen' : 'Mitspielen'"
          @click="p.playing = !p.playing"
        >
          <svg
            class="ico"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <template v-if="p.playing">
              <path d="M9.5 7v10M14.5 7v10" />
            </template>
            <path
              v-else
              d="M8 6.5v11l9-5.5-9-5.5Z"
              fill="currentColor"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          class="icon"
          aria-label="Entfernen"
          @click="remove(i)"
        >
          <svg
            class="ico"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="m7 7 10 10M17 7 7 17" />
          </svg>
        </button>
      </li>
    </ol>

    <div class="opts">
      <label class="opt">
        <input
          type="checkbox"
          :checked="props.celebrations === 'rare'"
          @change="emit('setCelebrations', props.celebrations === 'rare' ? 'all' : 'rare')"
        >
        <span>Weniger Jubel</span>
      </label>
      <label class="opt">
        <input
          type="checkbox"
          :checked="props.keepAwake"
          @change="emit('toggleKeepAwake', ($event.target as HTMLInputElement).checked)"
        >
        <span>Bildschirm anlassen</span>
      </label>
      <!-- FR-5.4: a game nobody finished should leave no trace. -->
      <button
        v-if="props.hasLiveGame"
        class="discard"
        @click="emit('discard')"
      >
        Laufendes Spiel verwerfen
      </button>
    </div>

    <div class="foot">
      <button
        class="secondary"
        :disabled="players.length < 2"
        @click="shuffle"
      >
        Mischen
      </button>
      <button
        class="primary"
        :disabled="players.filter((p) => p.playing).length < 2"
        @click="start"
      >
        Spiel starten
      </button>
    </div>
  </div>
</template>

<style scoped>
.setup {
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding: calc(var(--safe-top) + 6px) 16px calc(var(--safe-bottom) + 12px);
  padding-left: max(16px, var(--safe-left));
  padding-right: max(16px, var(--safe-right));
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 -4px 2px;
}

.nav-right {
  display: flex;
  gap: 2px;
}

.nav-btn {
  background: none;
  border: none;
  color: var(--accent);
  font-size: 15px;
  padding: 8px 4px;
  min-height: 44px;
}

.head h1 {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
}

.group-name {
  font: inherit;
  color: inherit;
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-height: 44px;
}

.rename-group {
  width: 100%;
  border: none;
  border-radius: 10px;
  background: var(--accent-tint);
  color: var(--text);
  font-size: 24px;
  font-weight: 700;
  padding: 4px 10px;
  min-height: 44px;
}

.sub {
  margin: 2px 0 14px;
  color: var(--text-2);
  font-size: 14px;
}

.add {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.input {
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: var(--radius-control);
  background: var(--card);
  color: var(--text);
  /* 16px or larger, otherwise iOS zooms the page when the field is focused. */
  font-size: 16px;
  padding: 12px 14px;
  min-height: 44px;
}

.add-btn {
  border: none;
  border-radius: var(--radius-control);
  background: var(--accent-tint);
  color: var(--accent);
  font-weight: 600;
  padding: 0 14px;
  min-height: 44px;
}

.add-btn:disabled {
  opacity: 0.4;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.row {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--card);
  border-radius: var(--radius-card);
  padding: 6px 10px;
  margin-bottom: 6px;
  min-height: 48px;
}

.row.out {
  opacity: 0.45;
}

.starter {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--accent);
  background: var(--accent-tint);
  border-radius: 999px;
  padding: 2px 7px;
  flex: none;
}

.pos {
  width: 1.4em;
  color: var(--text-3);
  font-size: 13px;
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  text-align: left;
  border: none;
  background: none;
  padding: 0;
  min-height: 36px;
  position: relative;
}

/* The row is only 36px tall, so the name reaches its 44px target the same way
   the icon buttons do -- extra hit area without extra visual weight. */
.name::after {
  content: "";
  position: absolute;
  inset: -4px;
}

.icon {
  border: none;
  background: none;
  color: var(--accent);
  font-size: 15px;
  /* 44 px of touch target without 44 px of visual weight. */
  width: 32px;
  height: 32px;
  border-radius: 8px;
  position: relative;
}

.icon::after {
  content: "";
  position: absolute;
  inset: -6px;
}

.ico {
  width: 20px;
  height: 20px;
  display: block;
  margin: 0 auto;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

.icon:disabled {
  color: var(--text-3);
}

.rename {
  flex: 1;
  min-width: 0;
  border: none;
  border-radius: 8px;
  background: var(--accent-tint);
  color: var(--text);
  /* 16px or iOS zooms the page when the field takes focus. */
  font-size: 16px;
  padding: 6px 8px;
}

.opts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 16px;
  padding: 6px 2px 0;
}

.opt {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-2);
  /* The label is the real target -- tapping it toggles the box -- so it carries
     the 44px, and the box itself grows a matching hit area. */
  min-height: 44px;
  cursor: pointer;
}

.opt input {
  width: 20px;
  height: 20px;
  position: relative;
  accent-color: var(--accent);
}

.opt input::after {
  content: "";
  position: absolute;
  inset: -12px;
}

.discard {
  border: none;
  background: none;
  color: var(--danger);
  font-size: 13px;
  padding: 8px 2px;
  min-height: 44px;
}

.foot {
  display: grid;
  grid-template-columns: 1fr 1.6fr;
  gap: 8px;
  padding-top: 10px;
}

.secondary,
.primary {
  border: none;
  border-radius: var(--radius-control);
  min-height: 50px;
  font-size: 17px;
  font-weight: 600;
}

.secondary {
  background: var(--card);
  color: var(--accent);
}

.primary {
  background: var(--accent);
  color: #fff;
}

.secondary:disabled,
.primary:disabled {
  opacity: 0.4;
}
</style>
